import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { CSSProperties } from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import commonEssence from '@sorare/core/src/assets/rewards/common-essence.png';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyL, HeadlineL } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { CardImg } from '@sorare/core/src/components/card/CardImg';
import { InteractiveStackedCards } from '@sorare/core/src/components/cards/InteractiveStackedCards';
import Dialog from '@sorare/core/src/components/dialog';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useIsMediumMobileHeightAndAbove } from '@sorare/core/src/hooks/device/useIsMediumMobileHeightAndAbove';
import useMutation from '@sorare/core/src/hooks/graphql/useMutation';
import { glossary } from '@sorare/core/src/lib/glossary';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import {
  BuyCardPackMutation,
  BuyCardPackMutationVariables,
  PackOpeningDialog_cardPack,
} from './__generated__/index.graphql';

const StyledDialog = styled(Dialog)`
  background: linear-gradient(180deg, var(--c-nl-200), var(--c-nl-200)),
    linear-gradient(180deg, var(--background-color) 0%, var(--c-nl-800) 100%);
`;
const Root = styled(Vertical).attrs({ gap: 0 })`
  min-height: 100%;
`;
const Content = styled(Vertical).attrs({ center: true })`
  justify-content: space-between;
  padding: var(--double-unit);
  padding-bottom: var(--quadruple-unit);
  gap: var(--double-unit);
  flex: 1;

  @media ${laptopAndAbove} {
    gap: calc(6 * var(--unit));
    min-height: min(calc(100vh - var(--unit) * 12), 600px);
  }
`;
const Img = styled(CardImg)`
  width: 65%;
  max-width: 240px;
  transform: rotate(3deg);
`;
const Header = styled.div`
  display: grid;
  grid-template-columns: 80px 1fr 80px;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: var(--double-unit);

  & > *:first-child {
    justify-self: flex-start;
  }
  & > *:last-child {
    justify-self: flex-end;
  }
`;
const StyledButton = styled(Button)`
  && {
    min-width: 200px;
  }
`;
const StyledLoadingButton = styled(LoadingButton)`
  && {
    min-width: 200px;
  }
`;

const BUY_CARD_PACK_MUTATION = gql`
  mutation BuyCardPackMutation($input: buyCardPackInput!, $sport: Sport!) {
    buyCardPack(input: $input) {
      currentUser {
        slug
        commonCardPoints(sport: $sport)
      }
      cards {
        slug
        pictureUrl
      }
      errors {
        code
        message
      }
    }
  }
` as TypedDocumentNode<BuyCardPackMutation, BuyCardPackMutationVariables>;

type Props = {
  open: boolean;
  onClose: () => void;
  cardPack: PackOpeningDialog_cardPack;
  groupColor: string;
};
export const PackOpeningDialog = ({
  open,
  onClose,
  cardPack,
  groupColor,
}: Props) => {
  const { pictureUrl, price } = cardPack;
  const [buyCardPack, { loading }] = useMutation(BUY_CARD_PACK_MUTATION, {
    showErrorsWithSnackNotification: true,
  });
  const isMediumMobileHeightAndAbove = useIsMediumMobileHeightAndAbove();
  const [cards, setCards] = useState<
    NonNullable<NonNullable<BuyCardPackMutation['buyCardPack']>['cards']>
  >([]);
  const { currentUser } = useCurrentUserContext();
  const { commonCardPoints } = currentUser || {};

  const onPurchase = async () => {
    const { data } = await buyCardPack({
      variables: {
        input: { cardPackSlug: cardPack.slug, sport: Sport.FOOTBALL },
        sport: Sport.FOOTBALL,
      },
    });
    setCards(data?.buyCardPack?.cards || []);
  };

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      hideHeader
      fullWidth
      maxWidth="xs"
      style={{ '--background-color': groupColor } as CSSProperties}
    >
      {({ CloseButton }) => (
        <Root>
          <Header>
            <Horizontal center>
              <img src={commonEssence} alt="" height={24} />
              <BodyL>{commonCardPoints}</BodyL>
            </Horizontal>
            <HeadlineL>
              <FormattedMessage
                id="PackOpeningDialog.title"
                defaultMessage="Buy pack"
              />
            </HeadlineL>
            <CloseButton onClose={onClose} />
          </Header>
          <Content>
            <Vertical center>
              <HeadlineL>Pack Name here</HeadlineL>
              <Chip>
                <Horizontal center>
                  <img src={commonEssence} alt="" height={24} />
                  <BodyL>{price}</BodyL>
                </Horizontal>
              </Chip>
            </Vertical>
            {cards.length === 0 ? (
              <>
                <Img src={pictureUrl} alt="" width={320} />
                <StyledLoadingButton
                  color="primary"
                  size="medium"
                  loading={loading}
                  onClick={onPurchase}
                >
                  <FormattedMessage {...glossary.buy} />
                </StyledLoadingButton>
              </>
            ) : (
              <>
                <InteractiveStackedCards
                  restack
                  cards={cards.map(card => card.pictureUrl).filter(Boolean)}
                  cardWidth={isMediumMobileHeightAndAbove ? 240 : 180}
                />
                <StyledButton onClick={onClose} size="medium">
                  <FormattedMessage {...glossary.continue} />
                </StyledButton>
              </>
            )}
          </Content>
        </Root>
      )}
    </StyledDialog>
  );
};

PackOpeningDialog.fragments = {
  cardPack: gql`
    fragment PackOpeningDialog_cardPack on CardPack {
      slug
      pictureUrl
      price
    }
  ` as TypedDocumentNode<PackOpeningDialog_cardPack>,
};
